import { useEffect, useState } from "react";
import Input from "@/app/components/ui/Inputs/Input";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useForm } from "react-hook-form";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { IShrinkUpdatePostBody } from "@/api_framework/api_modals/Inventory";
import { useShrinkReasonsListAPI } from "@/app/hooks/api_hooks/Inventory/GoodInventory/useShrinkReasonsListAPI";
import { useShrinkUpdateAPI } from "@/app/hooks/api_hooks/Inventory/GoodInventory/useShrinkUpdateAPI";

export interface IShrinkModalProps {
  qty: number;
  reason: string;
}

const ShrinkModalContent = ({ formHook, reasonOptions, forOnlyNumber, data }) => {
  return (
    <div>
      <div className="items-center gap-16 inline-flex">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px]">
          <span className="font-normal text-black text-xs">Product</span>
        </div>
        <p>{data?.product_name}</p>
      </div>
      <div className="gap-16 flex">
        <div className="p-2.5 w-[150px]">
          <span className="font-normal text-black text-xs">Lot Details</span>
        </div>
        <div className="flex mt-3 flex-col">
          <p>Lot: {data?.lot_number}</p>
          <p>Manufacturer: {data?.manufacturer_name}</p>
          <p>Exp Date: {data?.expiry_date}</p>
          <p>Mrp: {data?.mrp}</p>
        </div>
      </div>
      <div className="items-center gap-16 inline-flex">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px]">
          <span className="font-normal text-black text-xs">On hand:</span>
        </div>
        <p>{data?.qty}</p>
      </div>
      <div className="items-center gap-16 inline-flex mt-3">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px]">
          <span className="font-normal text-black text-xs">Move Qty</span>
          <span className="font-normal text-rose-600 text-xs">*</span>
        </div>
        <Input
          register={formHook.register("qty", {
            ...forOnlyNumber.validations,
            required: "Qty is required",
            min: {
              value: 1,
              message: "Qty must be a positive number",
            },
            max: {
              value: +data?.qty,
              message: "Qty must be less than or equal to Good / Sellable Qty",
            },
            validate: (value) => Number(value) > 0 || "Qty must be a positive number",
          })}
          type="number"
          min={1}
          max={+data?.qty}
          defaultValue={+data?.bad_qty}
          disabled={data.bad_qty > 0}
          errorMessages={[
            { type: "required", message: "Qty is required" },
            { type: "min", message: "Qty must be a positive number" },
            { type: "max", message: `Qty must be less than or equal to Good / Sellable Qty ${data?.qty}` },
          ]}
          className="h-9 w-[290px] text-xs rounded-md border-slate-400"
          fieldError={formHook.formState.errors.qty}
          placeholder="Enter Qty"
        />
      </div>
      <div className="justify-start items-center gap-16 inline-flex mt-5 mb-10">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[150px]">
          <span className="font-normal text-black text-xs">Provide Reason</span>
          <span className="font-normal text-rose-600 text-xs">*</span>
        </div>
        <div className="w-72">
          <SearchableSelectMenu
            errorMessages={[{ type: "required", message: "Reason is required" }]}
            onSelectItem={(item) => {
              if (item) {
                formHook.setValue("reason", item.title);
                formHook.clearErrors("reason");
              }
            }}
            fieldError={formHook.formState.errors.reason}
            register={formHook.register("reason", { required: true })}
            selectItems={reasonOptions}
            placeholder="Select Reason"
            showTooltips={false}
            showTypedErrors={true}
            showDropdownIcon={true}
            defaultSelected={reasonOptions?.filter((oc) => oc.title === formHook.watch("reason"))[0]}
            disabled={data.bad_qty > 0}
            listBoxClassName="w-full"
            className="text-gray-800"
            containerClassName="w-full"
          />
        </div>
      </div>
    </div>
  );
};

const ShrinkModal = () => {
  const { setInvisible, setModalData } = useModalActions("SHRINK_MODAL");
  const { execute: shrink, status } = useShrinkUpdateAPI();
  const formHook = useForm<IShrinkModalProps>({
    mode: "onChange",
  });
  const { shrinkReasonsList } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  const reasonOptions = useSelectMenuReducer(shrinkReasonsList, "reason", "id");
  const { forOnlyNumber } = useFormValidations();
  const modalState = useModalState("SHRINK_MODAL");
  const { unique_row_id } = modalState || {};
  const [isloading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchShrinkReasonsList();
  }, []);

  useEffect(() => {
    if (+modalState?.bad_qty > 0 && !modalState.qty) {
      formHook.setValue("qty", +modalState?.bad_qty);
      formHook.setValue("reason", modalState?.bad_reason);
    }
    setIsLoading(false);
  }, [modalState]);

  useEffect(() => {
    formHook.clearErrors();
    if (status) {
      formHook.setValue("qty", null);
      formHook.setValue("reason", null);
      setModalData({ status: true });
    } else {
      console.log("status false");
      setIsLoading(false);
    }
  }, [status]);

  const fetchShrinkReasonsList = useShrinkReasonsListAPI();

  const handleSubmit = async (data: IShrinkModalProps) => {
    if (isloading) return;
    const isValid = await formHook.trigger();
    let badValid = false;
    if (+modalState?.bad_qty > 0) {
      formHook.clearErrors("qty");
      formHook.clearErrors("reason");
      badValid = true;
    }
    setIsLoading(true);
    if (isValid || badValid) {
      const moveBody: IShrinkUpdatePostBody = {
        unique_row_id: unique_row_id,
        quantity: Number(data?.qty),
        reason: data?.reason,
      };
      try {
        await shrink(moveBody, setInvisible).finally(() => setModalData({ status: true }));
      } catch (error) {
        setIsLoading(false);
      }
    }
  };

  return (
    <ModalPrompt
      modalId="SHRINK_MODAL"
      showIcon={false}
      titleIcon=""
      dialogClassName="sm:my-8 sm:w-full sm:max-w-2xl sm:p-6"
      button={{
        primaryButtonText: "Update",
        secondaryButtonText: "Cancel",
        primaryButtonClassname: `px-6 py-[9px] rounded-md border border-slate-400 border-opacity-20 justify-center items-center gap-2 flex ${isloading ? "cursor-not-allowed bg-gray-400" : "cursor-pointer bg-sky-900 hover"}`,
        primaryCallback: () => {
          handleSubmit(formHook.getValues());
        },
        secondaryCallback: () => {
          formHook.clearErrors();
          formHook.setValue("qty", null);
          formHook.setValue("reason", null);
          setInvisible();
        },
        primaryButtonDisabled: isloading,
      }}
      title="Shrink"
      content={
        <ShrinkModalContent
          formHook={formHook}
          reasonOptions={reasonOptions}
          forOnlyNumber={forOnlyNumber}
          data={modalState}
        />
      }
    />
  );
};

export default ShrinkModal;
